/*
  menara-pdp-tableau.js — fiche d'un tableau (sections/menara-pdp-tableau.liquid).

  Les données viennent de Liquid (variantes réelles, prix déjà formatés, fiches
  sœurs). Ce script :
    - accorde les boutons aux variantes et marque les combinaisons absentes ;
    - tient le prix, le bouton, la barre du téléphone et l'adresse ?variant= ;
    - fait pointer chaque carte de support vers la variante équivalente de la
      fiche sœur (même taille, même cadre quand elle les propose) ;
    - pose le tableau à sa vraie largeur sur la pièce dessinée de la vue
      « à l'échelle » ;
    - garde l'envoi tant qu'aucune variante vendable n'est choisie.
  L'ajout au panier reste celui du thème (product-form-component).
*/
import { StandardEvents } from '@shopify/events';

/*
  Changement de support sans quitter la page.

  Chaque support est un produit Shopify distinct — c'est Gelato qui l'impose,
  une fabrication par support. Plutôt que de laisser le navigateur charger la
  fiche sœur, on va chercher ses sections et on les remplace sur place :
  le client ne voit pas de saut de page, l'adresse se met à jour, et le bouton
  Retour fonctionne. Si quoi que ce soit échoue, on suit le lien normalement.
*/
const SECTIONS_FICHE = '.mpt, .mta, .mtt';

const sectionsProduit = () =>
  Array.from(document.querySelectorAll('[id^="shopify-section-"]')).filter((sec) => sec.querySelector(SECTIONS_FICHE));

async function echangerFiche(href, { pousser = true } = {}) {
  const racines = sectionsProduit();
  if (!racines.length) return false;
  racines.forEach((r) => r.setAttribute('aria-busy', 'true'));
  try {
    const reponse = await fetch(href, { headers: { 'X-Requested-With': 'fetch' } });
    if (!reponse.ok) throw new Error('HTTP ' + reponse.status);
    const doc = new DOMParser().parseFromString(await reponse.text(), 'text/html');
    let remplacees = 0;
    racines.forEach((r) => {
      const neuf = doc.getElementById(r.id);
      if (!neuf) {
        r.removeAttribute('aria-busy');
        return;
      }
      r.replaceWith(neuf);
      remplacees += 1;
    });
    if (!remplacees) throw new Error('sections introuvables');
    if (doc.title) document.title = doc.title;
    if (pousser) window.history.pushState({ menaraFiche: true }, '', href);
    // Le clavier suit le support choisi.
    const choisi = document.querySelector('.mpt__support.est-choisi');
    if (choisi && document.activeElement === document.body && choisi.focus) choisi.focus({ preventScroll: true });
    return true;
  } catch (erreur) {
    console.warn('[fiche tableau] changement de support', erreur);
    sectionsProduit().forEach((r) => r.removeAttribute('aria-busy'));
    return false;
  }
}

document.addEventListener('click', (e) => {
  const lien = e.target.closest ? e.target.closest('a[data-mpt-support]') : null;
  if (!lien || e.defaultPrevented) return;
  // Clic du milieu, Ctrl, Cmd… : on laisse le navigateur ouvrir un onglet.
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const href = lien.getAttribute('href');
  if (!href) return;
  e.preventDefault();
  if (lien.getAttribute('aria-current') === 'page') return;
  lien.classList.add('est-en-attente');
  // Filet : si la réponse tarde, on suit le lien comme un lien ordinaire.
  const secours = setTimeout(() => {
    window.location.href = href;
  }, 4000);
  echangerFiche(href).then((ok) => {
    clearTimeout(secours);
    if (!ok) window.location.href = href;
  });
});

window.addEventListener('popstate', () => {
  if (!document.querySelector('.mpt')) return;
  echangerFiche(window.location.href, { pousser: false }).then((ok) => {
    if (!ok) window.location.reload();
  });
});

/** Surface d'un libellé de taille (« 60 × 90 cm » → 5400), 0 s'il n'en est pas un. */
const surface = (taille) => {
  const m = String(taille || '').match(/(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(',', '.')) * parseFloat(m[2].replace(',', '.')) : 0;
};

class MenaraPdpTableau extends HTMLElement {
  connectedCallback() {
    if (this.demarre) return;
    const source = this.querySelector('[data-mpt-donnees]');
    if (!source) return;
    try {
      this.d = JSON.parse(source.textContent);
    } catch (erreur) {
      console.warn('[fiche tableau] données illisibles', erreur);
      return;
    }
    this.demarre = true;
    this.nettoyeurs = [];
    this.variantes = this.d.variantes || [];
    this.L = this.d.libelles || {};
    this.nbOptions = (this.d.options || []).length;

    this.form = this.querySelector('form');
    this.radios = Array.from(this.querySelectorAll('[data-mpt-option]'));
    this.champId = this.querySelector('[data-mpt-id]');
    this.bouton = this.querySelector('[data-mpt-ajouter]');
    this.boutonTexte = this.querySelector('[data-mpt-ajouter-texte]');
    this.annonce = this.querySelector('[data-mpt-annonce]');

    this.radios.forEach((r) =>
      this.ecouter(r, 'change', () => {
        if (r.checked) this.choisir(Number(r.dataset.mptOption), r.value);
      })
    );

    // Tant qu'aucune variante vendable n'est choisie, l'envoi n'atteint pas
    // le composant du thème : cette garde écoute la fenêtre, en capture.
    this.ecouter(
      window,
      'submit',
      (e) => {
        if (e.target !== this.form) return;
        const v = this.variante();
        if (v && v.dispo) return;
        e.preventDefault();
        e.stopImmediatePropagation();
      },
      true
    );

    this.brancherVues();
    this.brancherZoom();
    this.brancherVisuels();
    this.brancherBarre();
    this.brancherPanier();
    this.appliquer({ annoncer: false, url: false });
  }

  disconnectedCallback() {
    (this.nettoyeurs || []).forEach((f) => f());
    this.nettoyeurs = [];
    this.demarre = false;
  }

  ecouter(cible, type, fn, options) {
    cible.addEventListener(type, fn, options);
    this.nettoyeurs.push(() => cible.removeEventListener(type, fn, options));
  }

  /* — Sélection et variantes — */

  selection() {
    const sel = new Array(this.nbOptions).fill(null);
    this.radios.forEach((r) => {
      if (r.checked) sel[Number(r.dataset.mptOption)] = r.value;
    });
    return sel;
  }

  trouver(sel) {
    return this.variantes.find((v) => v.options.every((o, i) => sel[i] === null || o === sel[i])) || null;
  }

  variante() {
    const sel = this.selection();
    if (sel.some((o) => o === null)) return null;
    return this.variantes.find((v) => v.options.every((o, i) => o === sel[i])) || null;
  }

  choisir(i, valeur) {
    const sel = this.selection();
    sel[i] = valeur;
    // La combinaison n'existe pas (une taille sans ce cadre) : on garde le
    // choix qui vient d'être fait et on ajuste les autres au plus proche.
    const exacte = this.variantes.find((v) => v.options.every((o, k) => o === sel[k]));
    if (!exacte) {
      const proche =
        this.variantes.find((v) => v.dispo && v.options[i] === valeur) ||
        this.variantes.find((v) => v.options[i] === valeur);
      if (proche) {
        proche.options.forEach((o, k) => {
          const r = this.radios.find((x) => Number(x.dataset.mptOption) === k && x.value === o);
          if (r) r.checked = true;
        });
      }
    }
    this.appliquer({ annoncer: true, url: true });
  }

  appliquer({ annoncer, url }) {
    const v = this.variante();
    const sel = this.selection();

    // Marquer chaque valeur : existe-t-elle avec le reste de la sélection ?
    this.radios.forEach((r) => {
      const i = Number(r.dataset.mptOption);
      const essai = sel.slice();
      essai[i] = r.value;
      const existe = this.variantes.find((x) => x.options.every((o, k) => o === essai[k]));
      const label = r.nextElementSibling;
      const note = label && label.querySelector('[data-mpt-note]');
      let texte = '';
      if (!existe) texte = this.L.indisponible;
      else if (!existe.dispo) texte = this.L.epuise;
      if (label) label.classList.toggle('est-indispo', Boolean(texte));
      if (note) note.textContent = texte;
    });

    this.ajusterCadre(sel);
    this.poserEchelle(v);

    let etat = 'ok';
    if (!v) etat = 'choisir';
    else if (!v.dispo) etat = 'epuise';
    this.dataset.etat = etat;

    if (this.champId) this.champId.value = v ? v.id : '';
    const prix = v ? v.prix : '';
    this.querySelectorAll('[data-mpt-prix], [data-mpt-barre-prix]').forEach((e) => (e.textContent = prix));

    const texteBouton = etat === 'ok' ? this.L.ajouter : etat === 'epuise' ? this.L.epuise : this.L.choisir;
    if (this.bouton) {
      this.bouton.setAttribute('aria-disabled', etat === 'ok' ? 'false' : 'true');
      this.bouton.disabled = etat === 'epuise';
    }
    if (this.boutonTexte) this.boutonTexte.textContent = texteBouton;
    const barreBouton = this.querySelector('[data-mpt-barre-bouton]');
    const barreTexte = this.querySelector('[data-mpt-barre-texte]');
    if (barreBouton) barreBouton.setAttribute('aria-disabled', etat === 'ok' ? 'false' : 'true');
    if (barreTexte) barreTexte.textContent = texteBouton;

    // Barre : « Titre · 60 × 90 cm » puis « Toile encadrée · Bois ».
    const taille = this.querySelector('[data-mpt-barre-taille]');
    if (taille) taille.textContent = v && v.taille ? ` · ${v.taille}` : '';
    const config = this.querySelector('[data-mpt-barre-config]');
    if (config) config.textContent = [this.d.support, v && v.cadre].filter(Boolean).join(' · ');

    this.relierSupports(v);

    if (url && v) {
      const adresse = new URL(window.location.href);
      adresse.searchParams.set('variant', v.id);
      window.history.replaceState(window.history.state, '', adresse.toString());
    }
    if (annoncer && this.annonce) {
      this.annonce.textContent = v ? `${[v.taille, v.cadre].filter(Boolean).join(', ')} — ${v.prix}` : this.L.choisir;
    }
  }

  /**
   * L'étape « cadre » n'a de sens que si le support choisi propose vraiment des
   * cadres. Quand les quatre supports vivent dans une seule fiche, la toile et
   * le plexiglas n'ont qu'une valeur de façade (« Sans cadre ») : on masque
   * l'étape et on renumérote celles qui restent. Avec quatre fiches séparées,
   * l'étape n'existe tout simplement pas sur ces supports — rien à faire.
   */
  ajusterCadre(sel) {
    const etape = this.querySelector('.mpt__etape--cadre');
    if (etape) {
      const i = Number(etape.dataset.mptEtape);
      const neutres = (etape.dataset.mptNeutres || '')
        .split(',')
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean);
      const valeurs = this.variantes
        .filter((x) => x.options.every((o, k) => k === i || sel[k] === null || o === sel[k]))
        .map((x) => x.options[i]);
      const utiles = Array.from(new Set(valeurs)).filter((x) => !neutres.includes(String(x).toLowerCase()));
      etape.hidden = utiles.length === 0;
    }

    const etapes = Array.from(this.querySelectorAll('.mpt__etape')).filter((e) => !e.hidden);
    etapes.forEach((e, k) => {
      const titre = e.querySelector('.mpt__etape-titre');
      if (!titre) return;
      if (!titre.dataset.mptTitre) titre.dataset.mptTitre = titre.textContent.replace(/^\s*\d+\.\s*/, '');
      titre.textContent = `${k + 1}. ${titre.dataset.mptTitre}`;
    });
  }

  /**
   * Chaque carte de support mène à la variante équivalente de la fiche sœur :
   * même taille et même cadre si elle les a, sinon même taille. Si la taille
   * n'existe pas là-bas (70 × 100 cm vers la toile encadrée), on retombe sur
   * sa plus grande taille disponible, en gardant le cadre quand c'est possible ;
   * à défaut, sur sa variante par défaut. Jamais de lien vers une variante
   * absente.
   */
  relierSupports(v) {
    const famille = this.d.famille || [];
    this.querySelectorAll('[data-mpt-support]').forEach((lien) => {
      const f = famille[Number(lien.dataset.mptSupport)];
      if (!f || f.courant) return;
      const candidats = f.variantes || [];
      let cible = null;
      if (v) {
        cible =
          candidats.find((x) => x.taille === v.taille && x.cadre && x.cadre === v.cadre) ||
          candidats.find((x) => x.taille === v.taille) ||
          null;
        if (!cible) {
          const dispo = candidats.filter((x) => x.dispo);
          const plusGrande = (liste) =>
            liste.reduce((meilleure, x) => (surface(x.taille) > surface(meilleure ? meilleure.taille : '') ? x : meilleure), null);
          const memeCadre = v.cadre ? dispo.filter((x) => x.cadre === v.cadre) : [];
          cible = plusGrande(memeCadre) || plusGrande(dispo);
          if (cible && surface(cible.taille) === 0) cible = null;
        }
      }
      const adresse = new URL(f.url, window.location.origin);
      if (cible) adresse.searchParams.set('variant', cible.id);
      else adresse.searchParams.delete('variant');
      // En aperçu d'un autre gabarit (?view=…), garder l'aperçu d'une fiche
      // à l'autre : sans ça, changer de support ramènerait au gabarit en place.
      const vue = new URLSearchParams(window.location.search).get('view');
      if (vue) adresse.searchParams.set('view', vue);
      lien.href = adresse.pathname + adresse.search;
    });
  }

  /* — Les deux manières de voir : l'œuvre, ou la pièce à l'échelle — */

  /* — Zoom — */

  /**
   * Agrandit le visuel sans le sortir de son cadre : la loupe suit le pointeur
   * sur ordinateur, un appui l'allume puis le doigt promène le cadrage sur
   * téléphone. L'image est posée en « contain » : le pointeur est ramené dans
   * la surface réellement occupée par l'image, sinon on zoomerait sur du vide.
   * Au premier zoom, `sizes` est élargi pour que le navigateur aille chercher
   * la plus grande source du srcset — inutile de la charger avant.
   */
  brancherZoom() {
    const scene = this.querySelector('[data-mpt-scene][data-mpt-zoom]');
    if (!scene) return;
    const fin = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    const cadre = (vue) => {
      const img = vue.querySelector('.mpt__image');
      const r = vue.getBoundingClientRect();
      if (!img || !img.naturalWidth || !img.naturalHeight) return { r, x: 0, y: 0, l: r.width, h: r.height };
      const k = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
      const l = img.naturalWidth * k;
      const h = img.naturalHeight * k;
      return { r, x: (r.width - l) / 2, y: (r.height - h) / 2, l, h };
    };

    const viser = (vue, ev) => {
      const c = cadre(vue);
      if (!c.r.width || !c.r.height) return;
      const px = Math.min(Math.max(ev.clientX - c.r.left, c.x), c.x + c.l);
      const py = Math.min(Math.max(ev.clientY - c.r.top, c.y), c.y + c.h);
      const img = vue.querySelector('.mpt__image');
      if (img) img.style.transformOrigin = `${((px / c.r.width) * 100).toFixed(2)}% ${((py / c.r.height) * 100).toFixed(2)}%`;
    };

    const allumer = (vue, ev) => {
      const img = vue.querySelector('.mpt__image');
      if (img && img.srcset && img.sizes !== '250vw') img.sizes = '250vw';
      vue.classList.add('est-zoom');
      scene.classList.add('est-zoom');
      if (ev) viser(vue, ev);
    };

    const eteindre = (vue) => {
      vue.classList.remove('est-zoom');
      scene.classList.remove('est-zoom');
      const img = vue.querySelector('.mpt__image');
      if (img) img.style.transformOrigin = '';
    };

    this.zoomEteindre = () =>
      scene.querySelectorAll('.mpt__vue.est-zoom').forEach((v) => eteindre(v));

    scene.querySelectorAll('[data-mpt-vue]:not(.mpt__vue--echelle)').forEach((vue) => {
      this.ecouter(vue, 'pointerenter', (e) => {
        if (e.pointerType === 'touch' || !fin()) return;
        allumer(vue, e);
      });
      this.ecouter(vue, 'pointermove', (e) => {
        if (!vue.classList.contains('est-zoom')) return;
        if (e.pointerType !== 'touch') e.preventDefault();
        viser(vue, e);
      });
      this.ecouter(vue, 'pointerleave', () => eteindre(vue));
      this.ecouter(vue, 'click', (e) => {
        if (fin() && e.pointerType !== 'touch') return;
        if (vue.classList.contains('est-zoom')) eteindre(vue);
        else allumer(vue, e);
      });
    });

    this.ecouter(document, 'keydown', (e) => {
      if (e.key === 'Escape') this.zoomEteindre();
    });
  }

  brancherVues() {
    this.scene = this.querySelector('[data-mpt-scene]');
    this.echelle = this.querySelector('[data-mpt-echelle]');
    this.bascule = this.querySelector('.mpt__bascule');
    this.vuesImages = Array.from(this.querySelectorAll('[data-mpt-vue]'));
    if (!this.echelle || !this.bascule) return;
    this.querySelectorAll('[data-mpt-mode]').forEach((b) =>
      this.ecouter(b, 'click', () => this.basculer(b.dataset.mptMode))
    );
  }

  basculer(mode) {
    if (this.zoomEteindre) this.zoomEteindre();
    const vueEchelle = mode === 'echelle';
    this.querySelectorAll('[data-mpt-mode]').forEach((x) => {
      const actif = x.dataset.mptMode === mode;
      x.classList.toggle('est-active', actif);
      x.setAttribute('aria-pressed', actif ? 'true' : 'false');
    });
    if (this.echelle) this.echelle.hidden = !vueEchelle;
    if (this.scene) this.scene.classList.toggle('est-echelle', vueEchelle);
    (this.vuesImages || []).forEach((vue) => {
      vue.hidden = vueEchelle ? true : !vue.classList.contains('est-active');
    });
    const bande = this.querySelector('[data-mpt-bande]');
    if (bande) bande.hidden = vueEchelle;
    const vignettes = this.querySelector('.mpt__vignettes');
    if (vignettes) vignettes.hidden = vueEchelle;
  }

  /**
   * Pose le tableau sur le mur dessiné, à sa vraie largeur : le canapé fait
   * 200 cm, le mur visible en fait autant que le réglage le dit, et la taille
   * choisie se lit sur son libellé (« 60 × 90 cm »). Si ce libellé ne se lit
   * pas — un support sans dimensions —, la vue à l'échelle n'a rien à montrer
   * et la bascule disparaît plutôt que d'afficher une mesure fausse.
   */
  poserEchelle(v) {
    const zone = this.querySelector('[data-mpt-tableau]');
    if (!this.echelle || !zone) return;
    const m = String((v && v.taille) || '').match(/(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)/);
    if (!m) {
      if (this.bascule) this.bascule.hidden = true;
      this.basculer('oeuvre');
      return;
    }
    if (this.bascule) this.bascule.hidden = false;
    const mur = Number(this.echelle.dataset.mptMur) || 300;
    const haut = Number(this.echelle.dataset.mptHaut) || 210;
    const accroche = Number(this.echelle.dataset.mptAccroche) || 155;
    const l = parseFloat(m[1].replace(',', '.'));
    const h = parseFloat(m[2].replace(',', '.'));
    zone.style.width = `${((l / mur) * 100).toFixed(2)}%`;
    zone.style.aspectRatio = `${l} / ${h}`;
    zone.style.bottom = `${(((accroche - h / 2) / haut) * 100).toFixed(2)}%`;
    const libelle = this.querySelector('[data-mpt-echelle-taille]');
    if (libelle) libelle.textContent = v.taille;
  }

  /* — Visuels — */

  brancherVisuels() {
    // La vue à l'échelle n'est pas un visuel du produit : elle reste hors
    // du compte, sinon les flèches s'arrêteraient dessus.
    const vues = Array.from(this.querySelectorAll('[data-mpt-vue]')).filter(
      (v) => !v.hasAttribute('data-mpt-echelle')
    );
    const vignettes = Array.from(this.querySelectorAll('[data-mpt-vignette]'));
    if (!vues.length) return;
    this.vues = vues;
    this.vignettes = vignettes;
    this.iVisuel = Math.max(0, vues.findIndex((v) => v.classList.contains('est-active')));

    vignettes.forEach((b, k) => this.ecouter(b, 'click', () => this.montrerVisuel(k)));

    const prec = this.querySelector('[data-mpt-prec]');
    const suiv = this.querySelector('[data-mpt-suiv]');
    if (prec) this.ecouter(prec, 'click', () => this.montrerVisuel(this.iVisuel - 1));
    if (suiv) this.ecouter(suiv, 'click', () => this.montrerVisuel(this.iVisuel + 1));

    // Au clavier, les flèches parcourent les visuels quand le focus est dans
    // la galerie — jamais quand on remplit un champ.
    const galerie = this.querySelector('.mpt__galerie');
    if (galerie && vues.length > 1) {
      this.ecouter(galerie, 'keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        if (e.target.closest('input, textarea, select')) return;
        e.preventDefault();
        this.montrerVisuel(this.iVisuel + (e.key === 'ArrowRight' ? 1 : -1));
      });
    }

    this.brancherBande();
  }

  /**
   * Montre le visuel demandé. L'indice boucle : après le dernier vient le
   * premier. Choisir un visuel, c'est aussi revenir à la vue de l'œuvre.
   */
  montrerVisuel(k) {
    const vues = this.vues || [];
    if (!vues.length) return;
    const n = vues.length;
    const i = ((k % n) + n) % n;
    this.iVisuel = i;
    vues.forEach((vue, j) => {
      vue.hidden = j !== i;
      vue.classList.toggle('est-active', j === i);
    });
    (this.vignettes || []).forEach((x, j) => {
      x.classList.toggle('est-active', j === i);
      x.setAttribute('aria-pressed', j === i ? 'true' : 'false');
    });
    if (this.zoomEteindre) this.zoomEteindre();
    if (this.bascule) this.basculer('oeuvre');
    const active = (this.vignettes || [])[i];
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }

  /**
   * La bande de vignettes défile dans sa colonne. Ses flèches n'apparaissent
   * que si elle déborde vraiment — sur un tableau à trois visuels, elles ne
   * servent à rien et encombrent.
   */
  brancherBande() {
    const piste = this.querySelector('[data-mpt-piste]');
    const bande = this.querySelector('[data-mpt-bande]');
    if (!piste || !bande) return;
    const prec = bande.querySelector('[data-mpt-vig-prec]');
    const suiv = bande.querySelector('[data-mpt-vig-suiv]');
    if (!prec || !suiv) return;

    // Les flèches n'apparaissent que si la bande déborde vraiment : sur un
    // tableau à trois visuels elles n'auraient rien à faire défiler.
    const ajuster = () => {
      const marge = piste.scrollWidth - piste.clientWidth;
      const deborde = marge > 2;
      prec.hidden = !deborde;
      suiv.hidden = !deborde;
      if (!deborde) return;
      prec.disabled = piste.scrollLeft <= 1;
      suiv.disabled = piste.scrollLeft >= marge - 1;
    };

    const pas = () => Math.max(120, piste.clientWidth * 0.8);
    const calme = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Le défilement fluide dépend d'une animation du navigateur, qui ne part
    // pas toujours (onglet caché, mouvement réduit) ; et un défilement
    // programmé n'émet pas toujours l'événement « scroll ». On vérifie donc
    // nous-mêmes, puis on remet les flèches dans le bon état.
    const defiler = (sens) => {
      const depart = piste.scrollLeft;
      const cible = Math.max(0, Math.min(depart + sens * pas(), piste.scrollWidth - piste.clientWidth));
      if (calme) {
        piste.scrollLeft = cible;
        ajuster();
        return;
      }
      piste.scrollTo({ left: cible, behavior: 'smooth' });
      setTimeout(() => {
        if (Math.abs(piste.scrollLeft - depart) < 2) piste.scrollLeft = cible;
        ajuster();
      }, 400);
    };
    this.ecouter(prec, 'click', () => defiler(-1));
    this.ecouter(suiv, 'click', () => defiler(1));
    this.ecouter(piste, 'scroll', ajuster, { passive: true });
    ajuster();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(ajuster);
      ro.observe(piste);
      this.nettoyeurs.push(() => ro.disconnect());
    } else {
      this.ecouter(window, 'resize', ajuster);
    }
  }

  /* — Barre du téléphone — */

  brancherBarre() {
    const bouton = this.querySelector('[data-mpt-barre-bouton]');
    if (!bouton || !this.form) return;
    this.ecouter(bouton, 'click', () => {
      if (bouton.getAttribute('aria-disabled') === 'true') {
        const premiere = this.querySelector('.mpt__etape');
        if (premiere) premiere.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (this.form.requestSubmit) this.form.requestSubmit(this.bouton || undefined);
      else if (this.bouton) this.bouton.click();
    });
  }

  /* — Panier — */

  dire(texte) {
    if (this.annonce) this.annonce.textContent = texte || '';
  }

  brancherPanier() {
    const composant = this.querySelector('product-form-component');
    if (!composant) return;
    this.ecouter(document, StandardEvents.cartLinesUpdate, (e) => {
      if (!(e.target instanceof Node) || !composant.contains(e.target)) return;
      if (this.bouton) this.bouton.classList.add('est-en-cours');
      Promise.resolve(e.promise)
        .then((resultat) => {
          if (this.bouton) this.bouton.classList.remove('est-en-cours');
          if (resultat && resultat.detail && resultat.detail.didError) {
            this.dire(this.L.erreur);
            return;
          }
          this.dire(this.L.ajoute);
          const confirmation = this.querySelector('[data-mpt-confirmation]');
          if (confirmation) {
            confirmation.hidden = false;
            clearTimeout(this.minuteur);
            this.minuteur = setTimeout(() => (confirmation.hidden = true), 9000);
          }
          const barreTexte = this.querySelector('[data-mpt-barre-texte]');
          if (barreTexte) {
            barreTexte.textContent = this.L.ajoute;
            setTimeout(() => this.appliquer({ annoncer: false, url: false }), 2200);
          }
          // Le compteur de l'en-tête Menara se met à jour sur cet événement.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch((erreur) => {
          if (this.bouton) this.bouton.classList.remove('est-en-cours');
          console.warn('[fiche tableau] ajout au panier', erreur);
        });
    });
  }
}

if (!customElements.get('menara-pdp-tableau')) {
  customElements.define('menara-pdp-tableau', MenaraPdpTableau);
}
